import { IDataPoolEntry } from '@/types';

/**
 * Parse step_N prefix from citation path
 * @example parseStepPath("step_1.proposals[0].title") → { stepIndex: 0, innerPath: "proposals[0].title" }
 * @example parseStepPath("step_2[0].content") → { stepIndex: 1, innerPath: "[0].content" }
 */
export function parseStepPath(path: string): { stepIndex: number; innerPath: string } {
	// Match both "step_1.field" and "step_1[0].field" formats
	// eslint-disable-next-line security/detect-unsafe-regex
	const match = path.match(/^step_(\d+)([\.\[].*)?$/);
	if (match) {
		const innerPath = match[2] || '';
		// Remove leading dot if present, but keep leading bracket
		const cleanedPath = innerPath.startsWith('.') ? innerPath.slice(1) : innerPath;
		return { stepIndex: parseInt(match[1], 10) - 1, innerPath: cleanedPath };
	}
	return { stepIndex: 0, innerPath: path };
}

/**
 * Get value at a JSON path from the appropriate data pool entry's raw data
 * Supports both dot notation and bracket notation for arrays
 * @example getValueAtPath(entries, "step_1.proposals[0].title")
 */
export function getValueAtPath(entries: IDataPoolEntry[], path: string): unknown {
	const { stepIndex, innerPath } = parseStepPath(path);
	const entry = entries.find((e) => e.stepIndex === stepIndex);
	if (!entry) return undefined;

	const obj = entry.rawData;
	if (!innerPath || !obj) return undefined;

	const parts = innerPath
		.replace(/\[(\d+)\]/g, '.$1')
		.split('.')
		.filter(Boolean);
	let current: unknown = obj;

	for (const part of parts) {
		if (current === null || current === undefined) return undefined;
		if (typeof current !== 'object') return undefined;

		current = (current as Record<string, unknown>)[part];
	}

	return current;
}

/**
 * Format a value for display - returns type-appropriate string representation
 */
export function formatValue(value: unknown): string {
	if (value === null) return 'null';
	if (value === undefined) return 'undefined';
	if (typeof value === 'string') return `"${value}"`;
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	if (Array.isArray(value)) return `Array(${value.length})`;
	if (typeof value === 'object') return 'Object';
	return String(value);
}

/**
 * Format a value for preview display with truncation
 * More verbose than formatValue, includes item/key counts
 */
export function formatValuePreview(value: unknown, maxLength = 100): string {
	if (value === null) return 'null';
	if (value === undefined) return 'undefined';
	if (typeof value === 'string') {
		const truncated = value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
		return `"${truncated}"`;
	}
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	if (Array.isArray(value)) return `Array with ${value.length} item${value.length !== 1 ? 's' : ''}`;
	if (typeof value === 'object') {
		const keys = Object.keys(value as object);
		return `Object with ${keys.length} key${keys.length !== 1 ? 's' : ''}`;
	}
	return String(value);
}
